import { useState, useMemo, useEffect } from 'react'
import { ArrowLeft, Trophy, TrendingUp, Calendar, MapPin, Clock, CheckCircle, Play, AlertCircle, X, RefreshCw, Share2 } from 'lucide-react'
import rasterizeHTML from 'rasterizehtml'

const ACTIVE_TAB_STORAGE_KEY = 'tournament-viewer-active-tab'

function CategoryView({ category, tournament, theme, exportedAt, onRefresh, onBack }) {
  // Initialize activeTab from localStorage if available
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY)
      return saved === 'standings' ? 'standings' : 'matches'
    } catch {
      return 'matches'
    }
  })
  
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [selectedStage, setSelectedStage] = useState(null)
  
  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab)
    } catch (err) {
      console.error('Failed to save active tab:', err)
    }
  }, [activeTab])
  
  // Force re-render when exportedAt changes
  const [dataVersion, setDataVersion] = useState(exportedAt)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshCounter, setRefreshCounter] = useState(0)
  
  // Share card as image using rasterizeHTML
  const handleShareCard = async (elementId, title) => {
    try {
      const element = document.getElementById(elementId)
      if (!element) {
        console.error('Element not found:', elementId)
        return
      }

      // Create a wrapper div off-screen
      const wrapper = document.createElement('div')
      wrapper.style.cssText = 'position: fixed; left: -9999px; top: 0; width: 1000px; background: white;'
      
      // Clone the element
      const clone = element.cloneNode(true)
      clone.style.cssText = 'margin: 0; width: 100%;'
      wrapper.appendChild(clone)
      
      document.body.appendChild(wrapper)
      
      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Get actual height
      const actualHeight = wrapper.offsetHeight
      const canvasHeight = Math.max(600, actualHeight + 100)
      
      // Get all styles
      const styles = Array.from(document.styleSheets)
        .map(sheet => {
          try {
            return Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n')
          } catch (e) {
            return ''
          }
        })
        .join('\n')

      // Create HTML
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            ${styles}
            body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; background: white; width: 1000px; }
          </style>
        </head>
        <body>
          ${clone.outerHTML}
        </body>
        </html>
      `
      
      // Remove wrapper
      document.body.removeChild(wrapper)
      
      // Create canvas
      const canvas = document.createElement('canvas')
      canvas.width = 1000
      canvas.height = canvasHeight
      
      // Render HTML to canvas
      await rasterizeHTML.drawHTML(html, canvas, {
        width: 1000,
        zoom: 1
      })
      
      // Convert to blob
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png', 0.95)
      })
      
      if (!blob || blob.size === 0) {
        throw new Error('Failed to generate image')
      }
      
      const file = new File([blob], `${title}.png`, { type: 'image/png' })
      
      // Try Web Share API
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: title,
            text: `Check out ${title} from ${tournament.name}!`
          })
        } catch (err) {
          if (err.name !== 'AbortError') {
            downloadImage(blob, title)
          }
        }
      } else {
        downloadImage(blob, title)
      }
    } catch (err) {
      console.error('Share failed:', err)
      alert('Failed to create image. Please try again.')
    }
  }
  
  // Download image fallback
  const downloadImage = (blob, title) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.download = `${title}.png`
    link.href = url
    link.click()
    URL.revokeObjectURL(url)
  }
  
  // Manual refresh handler
  const handleManualRefresh = async () => {
    console.log('=== MANUAL REFRESH TRIGGERED ===')
    setIsRefreshing(true)
    
    // Call the refresh function from App to fetch fresh data
    if (onRefresh) {
      console.log('Fetching fresh data from server...')
      await onRefresh()
      console.log('Fresh data fetched!')
    }
    
    // Increment refresh counter to force complete re-render
    setRefreshCounter(prev => prev + 1)
    
    // Force data version update to trigger recalculation
    const newVersion = Date.now().toString()
    console.log('Setting new dataVersion:', newVersion)
    setDataVersion(newVersion)
    
    // Reset all local state to clear any cached data
    setSelectedTeam(null)
    setSelectedStage(null)
    setActiveTab('matches')
    
    console.log('State reset complete, view should update now')
    
    // Stop refresh animation after 1 second
    setTimeout(() => setIsRefreshing(false), 1000)
  }
  
  // Reset component state when data changes
  useEffect(() => {
    console.log('=== CHECKING DATA VERSION ===')
    console.log('Current dataVersion:', dataVersion)
    console.log('New exportedAt:', exportedAt)
    console.log('Are they different?', exportedAt !== dataVersion)
    
    if (exportedAt !== dataVersion) {
      console.log('=== DATA VERSION CHANGED ===')
      console.log('Old version:', dataVersion)
      console.log('New version:', exportedAt)
      setDataVersion(exportedAt)
      // Reset any local state that might cache old data
      setSelectedTeam(null)
      setSelectedStage(null)
    }
  }, [exportedAt]) // Only watch exportedAt, not dataVersion
  
  // Format the exported timestamp
  const formatExportedAt = (timestamp) => {
    if (!timestamp) return 'Unknown'
    try {
      const date = new Date(timestamp)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    } catch {
      return 'Unknown'
    }
  }

  // Helper function to safely render player names
  const getPlayerName = (player) => {
    if (!player) return 'Unknown'
    if (typeof player === 'string') return player
    if (typeof player === 'object' && player.firstName && player.lastName) {
      return `${player.firstName} ${player.lastName}`
    }
    if (typeof player === 'object' && player.firstName) {
      return player.firstName
    }
    return 'Unknown'
  }

  // Get unique stages from matches
  const stages = useMemo(() => {
    console.log('=== RECALCULATING STAGES ===')
    console.log('exportedAt:', exportedAt)
    console.log('category.matches.length:', category.matches?.length)
    console.log('First match status:', category.matches?.[0]?.status)
    console.log('Tournament customConfig:', tournament.customConfig)
    console.log('Category customConfig:', category.customConfig)
    const stageMap = new Map()
    
    // First, create stage map from config if available to preserve order
    // Check category config first, then tournament config
    const stagesConfig = category.customConfig?.stages || tournament.customConfig?.stages
    
    if (stagesConfig) {
      console.log('Config stages:', stagesConfig)
      stagesConfig.forEach(stageConfig => {
        stageMap.set(stageConfig.name, {
          name: stageConfig.name,
          matches: [],
          order: typeof stageConfig.order === 'number' ? stageConfig.order : 0
        })
      })
    }
    
    // Then add matches to stages
    category.matches.forEach(match => {
      const stageName = match.stageName || 'Main Stage'
      if (!stageMap.has(stageName)) {
        // For unconfigured stages, use a very low order so they appear last
        stageMap.set(stageName, {
          name: stageName,
          matches: [],
          order: -999
        })
      }
      stageMap.get(stageName).matches.push(match)
    })
    
    // Convert to array and sort by order descending (latest/highest order first)
    const stagesArray = Array.from(stageMap.values())
      .filter(stage => stage.matches.length > 0) // Only include stages with matches
      .sort((a, b) => {
        // Sort by order descending (higher order = more recent = shown first)
        return b.order - a.order
      })
    
    console.log('Stages after sorting:', stagesArray.map(s => ({ name: s.name, order: s.order, matches: s.matches.length })))
    
    return stagesArray
  }, [category, tournament, exportedAt, dataVersion, refreshCounter])

  // Check if a stage is complete
  const isStageComplete = (stage) => {
    return stage.matches.every(m => m.status === 'completed')
  }

  // Get advancing teams count - should be the next stage's numberOfTeams
  const getAdvancingTeamsCount = (stageName) => {
    const stagesConfig = category.customConfig?.stages || tournament.customConfig?.stages
    if (!stagesConfig) return null
    
    // Find current stage
    const currentStage = stagesConfig.find(s => s.name === stageName)
    if (!currentStage) return null
    
    // Find next stage (stage with order = currentStage.order + 1)
    const nextStage = stagesConfig.find(s => s.order === currentStage.order + 1)
    
    // If there's a next stage, use its numberOfTeams as the advancing count
    if (nextStage && nextStage.numberOfTeams) {
      return nextStage.numberOfTeams
    }
    
    // Fallback: use teamsAdvancingPerBracket * numberOfBrackets
    if (currentStage.teamsAdvancingPerBracket && currentStage.numberOfBrackets) {
      return currentStage.teamsAdvancingPerBracket * currentStage.numberOfBrackets
    }
    
    return null
  }

  // Get stage format (knockout or round_robin)
  const getStageFormat = (stageName) => {
    const stagesConfig = category.customConfig?.stages || tournament.customConfig?.stages
    if (!stagesConfig) return null
    const stageConfig = stagesConfig.find(s => s.name === stageName)
    return stageConfig?.format || null
  }

  // Calculate standings per stage
  const standingsByStage = useMemo(() => {
    console.log('=== RECALCULATING STANDINGS ===')
    console.log('exportedAt:', exportedAt)
    console.log('Number of stages:', stages.length)
    return stages.map(stage => {
      const teamStats = new Map()
      const advancingCount = getAdvancingTeamsCount(stage.name)
      const stageFormat = getStageFormat(stage.name)

      // Initialize all teams that played in this stage
      stage.matches.forEach(match => {
        if (!teamStats.has(match.team1Id)) {
          teamStats.set(match.team1Id, {
            teamId: match.team1Id,
            teamName: match.team1Name,
            player1Name: match.team1Player1,
            player2Name: match.team1Player2,
            bracketNumber: match.bracketNumber,
            wins: 0,
            losses: 0,
            pointsFor: 0,
            pointsAgainst: 0,
            matchesPlayed: 0
          })
        }
        if (!teamStats.has(match.team2Id)) {
          teamStats.set(match.team2Id, {
            teamId: match.team2Id,
            teamName: match.team2Name,
            player1Name: match.team2Player1,
            player2Name: match.team2Player2,
            bracketNumber: match.bracketNumber,
            wins: 0,
            losses: 0,
            pointsFor: 0,
            pointsAgainst: 0,
            matchesPlayed: 0
          })
        }
      })

      // Process completed matches in this stage
      stage.matches
        .filter(m => m.status === 'completed')
        .forEach(match => {
          const team1Stats = teamStats.get(match.team1Id)
          const team2Stats = teamStats.get(match.team2Id)

          if (team1Stats && team2Stats) {
            team1Stats.matchesPlayed++
            team2Stats.matchesPlayed++
            team1Stats.pointsFor += match.team1Score || 0
            team1Stats.pointsAgainst += match.team2Score || 0
            team2Stats.pointsFor += match.team2Score || 0
            team2Stats.pointsAgainst += match.team1Score || 0

            if (match.winnerId === match.team1Id) {
              team1Stats.wins++
              team2Stats.losses++
            } else if (match.winnerId === match.team2Id) {
              team2Stats.wins++
              team1Stats.losses++
            }
          }
        })

      // Check if this is the final stage (no more stages after this)
      const stagesConfig = category.customConfig?.stages || tournament.customConfig?.stages
      const currentStageConfig = stagesConfig?.find(s => s.name === stage.name)
      const isFinalStage = stagesConfig ? !stagesConfig.some(s => s.order > (currentStageConfig?.order || 0)) : false
      
      // Only apply battle for third logic to the final stage
      let hasBattleForThird = false
      let finalsMatch = null
      let thirdPlaceMatch = null
      
      if (isFinalStage) {
        // Derive if this final stage has a battle for third place
        const completedMatches = stage.matches.filter(m => m.status === 'completed')
        
        console.log('Final stage completed matches:', completedMatches.map(m => ({
          team1: m.team1Name,
          team2: m.team2Name,
          team1Id: m.team1Id,
          team2Id: m.team2Id,
          isThirdPlace: m.isThirdPlaceMatch,
          winner: m.winnerId,
          scheduledTime: m.scheduledTime
        })))
        
        if (completedMatches.length >= 2) {
          // Try to use isThirdPlaceMatch flag first
          thirdPlaceMatch = completedMatches.find(m => m.isThirdPlaceMatch === true)
          finalsMatch = completedMatches.find(m => m.isThirdPlaceMatch !== true)
          
          console.log('Flag-based identification:', {
            thirdPlaceFound: !!thirdPlaceMatch,
            finalsFound: !!finalsMatch
          })
          
          // Fallback: if flag not set, identify by checking previous round losers
          if (!thirdPlaceMatch || !finalsMatch) {
            console.log('Using fallback logic - checking previous round')
            
            // Get all matches from all stages to find previous round
            const allMatches = stages.flatMap(s => s.matches)
            const currentRound = stage.order
            const previousRound = currentRound - 1
            
            console.log(`\n=== VIEWER: PREVIOUS ROUND ANALYSIS ===`)
            console.log(`Current stage: ${stage.name}, Current round: ${currentRound}`)
            console.log(`Looking for matches with round: ${previousRound}`)
            
            // Find matches from previous round
            const previousRoundMatches = allMatches.filter(m => 
              m.round === previousRound && 
              m.status === 'completed'
            )
            
            console.log(`Found ${previousRoundMatches.length} completed matches in round ${previousRound}`)
            
            previousRoundMatches.forEach((m, index) => {
              const winner = m.team1Id === m.winnerId ? m.team1Name : m.team2Name
              const loser = m.team1Id === m.winnerId ? m.team2Name : m.team1Name
              const winnerId = m.winnerId
              const loserId = m.team1Id === m.winnerId ? m.team2Id : m.team1Id
              
              console.log(`Match ${index + 1}: ${m.team1Name} vs ${m.team2Name}`)
              console.log(`  Winner: ${winner} (ID: ${winnerId})`)
              console.log(`  Loser: ${loser} (ID: ${loserId})`)
            })
            
            // Get losers from previous round (teams that are NOT winners)
            const previousLosers = previousRoundMatches.map(m => {
              const loserId = m.team1Id === m.winnerId ? m.team2Id : m.team1Id
              const loserName = m.team1Id === m.winnerId ? m.team2Name : m.team1Name
              return { id: loserId, name: loserName }
            })
            
            console.log(`\nPrevious round losers:`, previousLosers)
            
            // Third place match has both teams from previous round losers
            console.log(`\nChecking current round matches:`)
            thirdPlaceMatch = completedMatches.find(m => {
              const team1IsLoser = previousLosers.some(l => l.id === m.team1Id)
              const team2IsLoser = previousLosers.some(l => l.id === m.team2Id)
              const bothLosers = team1IsLoser && team2IsLoser
              
              console.log(`  ${m.team1Name} vs ${m.team2Name}:`)
              console.log(`    Team 1 (${m.team1Id}) is loser: ${team1IsLoser}`)
              console.log(`    Team 2 (${m.team2Id}) is loser: ${team2IsLoser}`)
              console.log(`    Both are losers: ${bothLosers}`)
              
              return bothLosers
            })
            
            // Finals match has teams that are NOT both losers
            finalsMatch = completedMatches.find(m => m.id !== thirdPlaceMatch?.id)
            
            console.log(`\nIdentified matches:`)
            console.log(`  Third Place: ${thirdPlaceMatch ? `${thirdPlaceMatch.team1Name} vs ${thirdPlaceMatch.team2Name}` : 'Not found'}`)
            console.log(`  Finals: ${finalsMatch ? `${finalsMatch.team1Name} vs ${finalsMatch.team2Name}` : 'Not found'}`)
            console.log(`=== END VIEWER: PREVIOUS ROUND ANALYSIS ===\n`)
          }
          
          hasBattleForThird = !!thirdPlaceMatch
        } else if (completedMatches.length === 1) {
          // Only one match - must be the finals
          finalsMatch = completedMatches[0]
          hasBattleForThird = false
        }
        
        console.log('Final identification:', {
          finalsFound: !!finalsMatch,
          finalsTeams: finalsMatch ? `${finalsMatch.team1Name} vs ${finalsMatch.team2Name}` : null,
          thirdPlaceFound: !!thirdPlaceMatch,
          thirdPlaceTeams: thirdPlaceMatch ? `${thirdPlaceMatch.team1Name} vs ${thirdPlaceMatch.team2Name}` : null
        })
      }
      
      // Convert to array and sort
      let standings = Array.from(teamStats.values())
      
      if (hasBattleForThird && finalsMatch && thirdPlaceMatch) {
        // Special ranking for finals with battle for third
        console.log('Finals Match:', {
          team1: finalsMatch.team1Name,
          team2: finalsMatch.team2Name,
          winner: finalsMatch.winnerId,
          isThirdPlace: finalsMatch.isThirdPlaceMatch
        })
        console.log('Third Place Match:', {
          team1: thirdPlaceMatch.team1Name,
          team2: thirdPlaceMatch.team2Name,
          winner: thirdPlaceMatch.winnerId,
          isThirdPlace: thirdPlaceMatch.isThirdPlaceMatch
        })
        
        // Determine the loser of the finals match
        const finalsLoserId = finalsMatch.team1Id === finalsMatch.winnerId 
          ? finalsMatch.team2Id 
          : finalsMatch.team1Id
        
        // Determine the loser of the third place match
        const thirdPlaceLoserId = thirdPlaceMatch.team1Id === thirdPlaceMatch.winnerId
          ? thirdPlaceMatch.team2Id
          : thirdPlaceMatch.team1Id
        
        console.log('Ranking IDs:', {
          rank1: finalsMatch.winnerId,
          rank2: finalsLoserId,
          rank3: thirdPlaceMatch.winnerId,
          rank4: thirdPlaceLoserId
        })
        
        standings = standings.map(team => {
          if (team.teamId === finalsMatch.winnerId) {
            return { ...team, rank: 1 }
          } else if (team.teamId === finalsLoserId) {
            return { ...team, rank: 2 } // Loser of finals
          } else if (team.teamId === thirdPlaceMatch.winnerId) {
            return { ...team, rank: 3 }
          } else if (team.teamId === thirdPlaceLoserId) {
            return { ...team, rank: 4 }
          } else {
            // Fallback: shouldn't happen but just in case
            return { ...team, rank: 5 }
          }
        }).sort((a, b) => a.rank - b.rank)
      } else {
        // Normal sorting by wins and point differential (for all other stages)
        standings.sort((a, b) => {
          if (b.wins !== a.wins) return b.wins - a.wins
          const aDiff = a.pointsFor - a.pointsAgainst
          const bDiff = b.pointsFor - b.pointsAgainst
          if (bDiff !== aDiff) return bDiff - aDiff
          return b.pointsFor - a.pointsFor
        })
      }

      // For knockout stages, derive advancing count if not explicitly set
      let finalAdvancingCount = advancingCount
      if (stageFormat === 'knockout' && !advancingCount) {
        // In knockout, winners advance (top 50% of teams)
        const totalTeams = standings.length
        finalAdvancingCount = Math.ceil(totalTeams / 2)
      }

      // Group by bracket ONLY if not final stage
      // For final stage, keep teams in ranked order
      const brackets = new Map()
      if (isFinalStage) {
        // Don't group by bracket for final stage - keep ranked order
        brackets.set(1, standings)
      } else {
        // Group by bracket for non-final stages
        standings.forEach(team => {
          const bracketNum = team.bracketNumber || 1
          if (!brackets.has(bracketNum)) {
            brackets.set(bracketNum, [])
          }
          brackets.get(bracketNum).push(team)
        })
      }

      // Calculate which teams are actually advancing based on overall ranking across all brackets
      // This matches the tournament management logic
      const advancingTeamIds = new Set()
      if (!isFinalStage && finalAdvancingCount) {
        // Sort ALL teams across all brackets by overall ranking
        const allTeamsSorted = [...standings].sort((a, b) => {
          if (b.wins !== a.wins) return b.wins - a.wins
          const aDiff = a.pointsFor - a.pointsAgainst
          const bDiff = b.pointsFor - b.pointsAgainst
          if (bDiff !== aDiff) return bDiff - aDiff
          return b.pointsFor - a.pointsFor
        })
        
        // Take the top N teams across all brackets
        const advancingTeams = allTeamsSorted.slice(0, Math.min(finalAdvancingCount, allTeamsSorted.length))
        advancingTeams.forEach(team => advancingTeamIds.add(team.teamId))
      }

      return {
        stageName: stage.name,
        isComplete: isStageComplete(stage),
        brackets: Array.from(brackets.entries()).sort((a, b) => a[0] - b[0]),
        advancingCount: finalAdvancingCount,
        advancingTeamIds,
        stageFormat,
        isFinalStage
      }
    })
  }, [stages, tournament, category, exportedAt, dataVersion, refreshCounter])

  // Sort matches by time
  const sortedMatches = useMemo(() => {
    return [...category.matches].sort((a, b) => {
      if (!a.scheduledTime) return 1
      if (!b.scheduledTime) return -1
      return new Date(a.scheduledTime) - new Date(b.scheduledTime)
    })
  }, [category.matches, exportedAt, dataVersion, refreshCounter])

  const formatTime = (dateString) => {
    if (!dateString) return 'TBD'
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    } catch {
      return 'TBD'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'TBD'
    }
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.gradient} pb-20`}>
      {/* Floating Back Button */}
      <button
        onClick={onBack}
        className="fixed top-4 right-4 z-20 flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 hover:text-gray-900 px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all font-semibold border-2 border-gray-200"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="hidden sm:inline">Back to Home</span>
      </button>

      {/* Header */}
      <div className={`bg-white shadow-lg sticky top-0 z-10 border-b-4 ${theme.border.primary}`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-start gap-4 mb-4">
            {tournament.logoUrl ? (
              <img
                src={tournament.logoUrl}
                alt="Tournament Logo"
                className={`w-16 h-16 object-contain rounded-lg ${theme.primaryShades[50]} p-2 border-2 ${theme.border.primary}`}
                onError={(e) => e.target.style.display = 'none'}
              />
            ) : (
              <div className={`w-16 h-16 bg-gradient-to-br ${theme.gradient} rounded-lg flex items-center justify-center shadow-lg`}>
                <Trophy className="w-8 h-8 text-white" />
              </div>
            )}
            
            <div className="flex-1">
              <h1 className="text-2xl font-black text-gray-800 leading-tight">
                {tournament.name}
              </h1>
              <div className="mt-1 text-xs space-y-0.5">
                <div className={`${theme.text.primary} font-bold`}>{theme.ribbon} {theme.message}</div>
                <div className="text-gray-500 font-semibold">Powered by PickleStack</div>
              </div>
            </div>
          </div>
          
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-2xl font-black text-gray-800">
                {category.title}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {exportedAt && (
                <div className="text-right">
                  <p className="text-xs text-gray-500 font-semibold">Data as of</p>
                  <p className={`text-sm ${theme.text.primary} font-bold`}>{formatExportedAt(exportedAt)}</p>
                </div>
              )}
              <button
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                className={`flex items-center gap-2 ${theme.primaryShades[600]} hover:${theme.primaryShades[700]} disabled:opacity-50 text-white px-4 py-2 rounded-lg font-bold transition-all shadow-md hover:shadow-lg`}
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="bg-white rounded-2xl shadow-xl p-2 flex gap-2">
          <button
            onClick={() => setActiveTab('matches')}
            className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
              activeTab === 'matches'
                ? `bg-gradient-to-r ${theme.gradient} text-white shadow-lg`
                : `text-gray-600 hover:${theme.primaryShades[50]}`
            }`}
          >
            <Trophy className="w-5 h-5 inline-block mr-2" />
            Matches
          </button>
          <button
            onClick={() => setActiveTab('standings')}
            className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${
              activeTab === 'standings'
                ? `bg-gradient-to-r ${theme.gradient} text-white shadow-lg`
                : `text-gray-600 hover:${theme.primaryShades[50]}`
            }`}
          >
            <TrendingUp className="w-5 h-5 inline-block mr-2" />
            Standings
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-6">
        {activeTab === 'standings' && (
          <div className="space-y-6">
            {standingsByStage.map((stageData) => (
              <div key={`${stageData.stageName}-${exportedAt}`} id={`standings-${stageData.stageName.replace(/\s+/g, '-')}`} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Stage Header */}
                <div className={`bg-gradient-to-r ${theme.gradient} text-white p-4`}>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-black">
                      {stageData.stageName}
                    </h2>
                    <div className="flex items-center gap-2">
                      {!stageData.isComplete && (
                        <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-xs font-bold">In Progress</span>
                        </div>
                      )}
                      {stageData.isComplete && (
                        <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-xs font-bold">Complete</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show standings only if stage is complete */}
                {stageData.isComplete ? (
                  <div className="p-4 bg-gray-50">
                    {/* Simplified Standings Table */}
                    {stageData.brackets.map(([bracketNum, teams]) => (
                      <div key={bracketNum} className="bg-white rounded-xl shadow-md overflow-hidden mb-4 last:mb-0 border border-gray-200">
                        {stageData.brackets.length > 1 && (
                          <div className={`bg-gradient-to-r ${theme.gradient} text-white p-3`}>
                            <h3 className="text-md font-black">Bracket {bracketNum}</h3>
                          </div>
                        )}
                      
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b-2 border-gray-200">
                            <tr>
                              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-600 uppercase">Rank</th>
                              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-600 uppercase">Team</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase">W-L</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase">PF</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase">PA</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase hidden sm:table-cell">Diff</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {teams.map((team, index) => {
                              const isAdvancing = stageData.advancingTeamIds && stageData.advancingTeamIds.has(team.teamId)
                              const isEliminated = !stageData.isFinalStage && stageData.advancingTeamIds && !stageData.advancingTeamIds.has(team.teamId)
                              
                              return (
                                <tr 
                                  key={team.teamId} 
                                  onClick={() => {
                                    setSelectedTeam(team)
                                    setSelectedStage(stageData.stageName)
                                  }}
                                  className={`transition-colors cursor-pointer ${
                                    isAdvancing ? `${theme.primaryShades[50]} hover:${theme.primaryShades[100]}` :
                                    isEliminated ? `${theme.secondaryShades[50]} hover:${theme.secondaryShades[100]}` :
                                    'hover:bg-gray-50'
                                  }`}>
                                  {/* Rank */}
                                  <td className="px-1 sm:px-2 py-2">
                                    <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-black text-xs sm:text-lg ${
                                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
                                      index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {index + 1}
                                    </div>
                                  </td>
                                  
                                  {/* Team Info */}
                                  <td className="px-1 sm:px-2 py-2">
                                    <h3 className={`font-bold text-xs sm:text-base leading-tight ${
                                      isAdvancing ? `${theme.text.primary}` : 'text-gray-800'
                                    }`}>
                                      {team.teamName}
                                    </h3>
                                    <p className="text-[10px] sm:text-sm text-gray-600 truncate leading-tight">
                                      {getPlayerName(team.player1Name)} / {getPlayerName(team.player2Name)}
                                    </p>
                                  </td>
                                  
                                  {/* W-L */}
                                  <td className="px-1 sm:px-2 py-2 text-center font-black text-gray-800 text-xs sm:text-sm">
                                    {team.wins}-{team.losses}
                                  </td>
                                  
                                  {/* PF */}
                                  <td className="px-1 sm:px-2 py-2 text-center font-black text-gray-800 text-xs sm:text-sm">
                                    {team.pointsFor}
                                  </td>
                                  
                                  {/* PA */}
                                  <td className="px-1 sm:px-2 py-2 text-center font-black text-gray-800 text-xs sm:text-sm">
                                    {team.pointsAgainst}
                                  </td>
                                  
                                  {/* Diff */}
                                  <td className="px-1 sm:px-2 py-2 text-center hidden sm:table-cell">
                                    <span className={`font-black text-xs sm:text-sm ${
                                      team.pointsFor - team.pointsAgainst > 0 ? 'text-green-600' :
                                      team.pointsFor - team.pointsAgainst < 0 ? 'text-red-600' :
                                      'text-gray-800'
                                    }`}>
                                      {team.pointsFor - team.pointsAgainst > 0 ? '+' : ''}
                                      {team.pointsFor - team.pointsAgainst}
                                    </span>
                                  </td>
                                  
                                  {/* Status */}
                                  <td className="px-1 sm:px-2 py-2 text-center">
                                    {stageData.isFinalStage ? (
                                      // Final stage: show placement
                                      <span className={`inline-flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold whitespace-nowrap ${
                                        index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white' :
                                        index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-white' :
                                        index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white' :
                                        'bg-gray-200 text-gray-700'
                                      }`}>
                                        <Trophy className="w-2 h-2 sm:w-3 sm:h-3" />
                                        <span className="hidden sm:inline">
                                          {index === 0 ? '1st Place' :
                                           index === 1 ? '2nd Place' :
                                           index === 2 ? '3rd Place' :
                                           `${index + 1}th Place`}
                                        </span>
                                        <span className="sm:hidden">
                                          {index === 0 ? '1st' :
                                           index === 1 ? '2nd' :
                                           index === 2 ? '3rd' :
                                           `${index + 1}th`}
                                        </span>
                                      </span>
                                    ) : (
                                      // Non-final stage: show advancing/eliminated
                                      <>
                                        {isAdvancing && (
                                          <span className={`inline-flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs ${theme.primaryShades[600]} text-white px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold whitespace-nowrap`}>
                                            <CheckCircle className="w-2 h-2 sm:w-3 sm:h-3" />
                                            <span className="hidden sm:inline">Advancing</span>
                                            <span className="sm:hidden">Adv</span>
                                          </span>
                                        )}
                                        {isEliminated && (
                                          <span className="inline-flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs bg-red-600 text-white px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold whitespace-nowrap">
                                            <X className="w-2 h-2 sm:w-3 sm:h-3" />
                                            <span className="hidden sm:inline">Eliminated</span>
                                            <span className="sm:hidden">Elim</span>
                                          </span>
                                        )}
                                        {!isAdvancing && !isEliminated && (
                                          <span className="text-[10px] sm:text-xs text-gray-400 font-semibold">—</span>
                                        )}
                                      </>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                  
                  {/* Overall Summary for Round Robin with multiple brackets */}
                  {stageData.stageFormat === 'round_robin' && stageData.brackets.length > 1 && (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                      <div className={`bg-gradient-to-r ${theme.gradient} text-white p-4`}>
                        <h3 className="text-lg font-black flex items-center gap-2">
                          <Trophy className="w-5 h-5" />
                          {stageData.stageName} - Overall Rankings
                        </h3>
                        <p className="text-sm opacity-80 mt-1">Combined standings across all brackets</p>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b-2 border-gray-200">
                            <tr>
                              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-600 uppercase">Rank</th>
                              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-600 uppercase">Team</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase">Bracket</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase">W-L</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase">PF</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase">PA</th>
                              <th className="px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold text-gray-600 uppercase hidden sm:table-cell">Diff</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {stageData.brackets
                              .flatMap(([bracketNum, teams]) => teams.map(team => ({ ...team, bracketNum })))
                              .sort((a, b) => {
                                if (b.wins !== a.wins) return b.wins - a.wins
                                const aDiff = a.pointsFor - a.pointsAgainst
                                const bDiff = b.pointsFor - b.pointsAgainst
                                if (bDiff !== aDiff) return bDiff - aDiff
                                return b.pointsFor - a.pointsFor
                              })
                              .map((team, index) => (
                                <tr 
                                  key={team.teamId} 
                                  onClick={() => {
                                    setSelectedTeam(team)
                                    setSelectedStage(stageData.stageName)
                                  }}
                                  className="hover:bg-gray-50 cursor-pointer"
                                >
                                  <td className="px-1 sm:px-2 py-2">
                                    <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-black text-xs sm:text-lg ${
                                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
                                      index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {index + 1}
                                    </div>
                                  </td>
                                  <td className="px-1 sm:px-2 py-2">
                                    <h3 className="font-bold text-gray-800 text-xs sm:text-base leading-tight">{team.teamName}</h3>
                                    <p className="text-[10px] sm:text-sm text-gray-600 truncate leading-tight">
                                      {getPlayerName(team.player1Name)} / {getPlayerName(team.player2Name)}
                                    </p>
                                  </td>
                                  <td className="px-1 sm:px-2 py-2 text-center">
                                    <span className="inline-block bg-indigo-100 text-indigo-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold whitespace-nowrap">
                                      <span className="hidden sm:inline">Bracket </span>
                                      {team.bracketNum}
                                    </span>
                                  </td>
                                  <td className="px-1 sm:px-2 py-2 text-center font-black text-gray-800 text-xs sm:text-sm">
                                    {team.wins}-{team.losses}
                                  </td>
                                  <td className="px-1 sm:px-2 py-2 text-center font-black text-gray-800 text-xs sm:text-sm">
                                    {team.pointsFor}
                                  </td>
                                  <td className="px-1 sm:px-2 py-2 text-center font-black text-gray-800 text-xs sm:text-sm">
                                    {team.pointsAgainst}
                                  </td>
                                  <td className="px-1 sm:px-2 py-2 text-center hidden sm:table-cell">
                                    <span className={`font-black text-xs sm:text-sm ${
                                      team.pointsFor - team.pointsAgainst > 0 ? 'text-green-600' :
                                      team.pointsFor - team.pointsAgainst < 0 ? 'text-red-600' :
                                      'text-gray-800'
                                    }`}>
                                      {team.pointsFor - team.pointsAgainst > 0 ? '+' : ''}
                                      {team.pointsFor - team.pointsAgainst}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                    <p className="text-gray-600 font-semibold">
                      Standings will be available when all matches in this stage are completed
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'matches' && (
          <div className="space-y-6">
            {stages.map((stage) => (
              <div key={`${stage.name}-${exportedAt}`} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Stage Header */}
                <div className={`bg-gradient-to-r ${theme.gradient} text-white p-4`}>
                  <h2 className="text-xl font-black">{stage.name}</h2>
                  <p className="text-sm opacity-80 mt-1">
                    {stage.matches.filter(m => m.status === 'completed').length} / {stage.matches.length} matches completed
                  </p>
                </div>

                {/* Matches in this stage */}
                <div className="p-4 space-y-4 bg-gray-50">
                  {stage.matches
                    .sort((a, b) => {
                      if (!a.scheduledTime) return 1
                      if (!b.scheduledTime) return -1
                      return new Date(a.scheduledTime) - new Date(b.scheduledTime)
                    })
                    .map((match) => (
              <div key={match.id} id={`match-${match.id}`} className="bg-white rounded-xl shadow-md overflow-hidden animate-content-update border border-gray-200">
                {/* Match Header */}
                <div className={`p-4 ${
                  match.status === 'completed' ? `bg-gradient-to-r ${theme.gradient}` :
                  match.status === 'in_progress' ? `${theme.primaryShades[500]}` :
                  'bg-gradient-to-r from-gray-400 to-gray-600'
                }`}>
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-2">
                      {match.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : match.status === 'in_progress' ? (
                        <Play className="w-5 h-5" />
                      ) : (
                        <Clock className="w-5 h-5" />
                      )}
                      <span className="font-bold text-sm">
                        {match.status === 'completed' ? 'COMPLETED' :
                         match.status === 'in_progress' ? 'IN PROGRESS' :
                         'SCHEDULED'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      {match.courtNumber && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          Court {match.courtNumber}
                        </div>
                      )}
                      {match.scheduledTime && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(match.scheduledTime)} {formatTime(match.scheduledTime)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Teams */}
                <div className="p-6">
                  {/* Team 1 */}
                  <div className={`flex items-center justify-between mb-4 pb-4 border-b-2 ${
                    match.winnerId === match.team1Id ? `${theme.border.primary}` : 'border-gray-200'
                  }`}>
                    <div className="flex-1">
                      <h3 className={`font-bold text-lg ${
                        match.winnerId === match.team1Id ? `${theme.text.primary}` : 'text-gray-800'
                      }`}>
                        {match.team1Name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {getPlayerName(match.team1Player1)} / {getPlayerName(match.team1Player2)}
                      </p>
                    </div>
                    {match.status !== 'scheduled' && (
                      <div className={`text-3xl font-black ${
                        match.winnerId === match.team1Id ? `${theme.text.primary}` : 'text-gray-400'
                      }`}>
                        {match.team1Score || 0}
                      </div>
                    )}
                  </div>

                  {/* Team 2 */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className={`font-bold text-lg ${
                        match.winnerId === match.team2Id ? `${theme.text.primary}` : 'text-gray-800'
                      }`}>
                        {match.team2Name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {getPlayerName(match.team2Player1)} / {getPlayerName(match.team2Player2)}
                      </p>
                    </div>
                    {match.status !== 'scheduled' && (
                      <div className={`text-3xl font-black ${
                        match.winnerId === match.team2Id ? `${theme.text.primary}` : 'text-gray-400'
                      }`}>
                        {match.team2Score || 0}
                      </div>
                    )}
                  </div>

                  {/* Game Scores */}
                  {match.games && match.games.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 font-semibold mb-2">GAME SCORES</p>
                      <div className="flex gap-2">
                        {match.games.map((game, index) => (
                          <div key={index} className="bg-gray-100 rounded-lg px-3 py-2 text-center">
                            <p className="text-xs text-gray-500 font-semibold">Game {index + 1}</p>
                            <p className="text-sm font-black text-gray-800">
                              {game.team1Score}-{game.team2Score}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
                </div>
              </div>
            ))}

            {category.matches.length === 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-semibold">No matches scheduled yet</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Team Match Details Modal */}
      {selectedTeam && selectedStage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => {
          setSelectedTeam(null)
          setSelectedStage(null)
        }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className={`bg-gradient-to-r ${theme.gradient} text-white p-6 sticky top-0`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-black">{selectedTeam.teamName}</h2>
                  <p className="opacity-90 text-sm mt-1">
                    {getPlayerName(selectedTeam.player1Name)} / {getPlayerName(selectedTeam.player2Name)}
                  </p>
                  <p className="opacity-80 text-xs mt-2">{selectedStage}</p>
                </div>
                <button 
                  onClick={() => {
                    setSelectedTeam(null)
                    setSelectedStage(null)
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Team Stats */}
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <p className="text-xs opacity-80">Wins</p>
                  <p className="text-2xl font-black">{selectedTeam.wins}</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <p className="text-xs opacity-80">Losses</p>
                  <p className="text-2xl font-black">{selectedTeam.losses}</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <p className="text-xs opacity-80">PF</p>
                  <p className="text-2xl font-black">{selectedTeam.pointsFor}</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <p className="text-xs opacity-80">PA</p>
                  <p className="text-2xl font-black">{selectedTeam.pointsAgainst}</p>
                </div>
              </div>
            </div>

            {/* Match List */}
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Match History</h3>
              <div className="space-y-4">
                {category.matches
                  .filter(m => 
                    m.stageName === selectedStage && 
                    (m.team1Id === selectedTeam.teamId || m.team2Id === selectedTeam.teamId)
                  )
                  .map(match => {
                    const isTeam1 = match.team1Id === selectedTeam.teamId
                    const opponentName = isTeam1 ? match.team2Name : match.team1Name
                    const teamScore = isTeam1 ? match.team1Score : match.team2Score
                    const opponentScore = isTeam1 ? match.team2Score : match.team1Score
                    const isWinner = match.winnerId === selectedTeam.teamId
                    
                    return (
                      <div key={match.id} className={`border-2 rounded-lg p-4 ${
                        match.status === 'completed' ? (isWinner ? `${theme.border.primary} ${theme.primaryShades[50]}` : `${theme.border.secondary} ${theme.secondaryShades[50]}`) : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {match.status === 'completed' && (
                              <div className={`w-2 h-2 rounded-full ${isWinner ? theme.primaryShades[600] : theme.secondaryShades[600]}`}></div>
                            )}
                            <span className="font-bold text-gray-800">vs {opponentName}</span>
                          </div>
                          {match.status === 'completed' && (
                            <div className="text-lg font-black">
                              <span className={isWinner ? `${theme.text.primary}` : 'text-gray-600'}>{teamScore}</span>
                              <span className="text-gray-400 mx-1">-</span>
                              <span className={!isWinner ? `${theme.text.secondary}` : 'text-gray-600'}>{opponentScore}</span>
                            </div>
                          )}
                        </div>
                        
                        {match.scheduledTime && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            {formatTime(match.scheduledTime)}
                          </div>
                        )}
                        
                        {match.games && match.games.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-500 font-semibold mb-2">Game Scores</p>
                            <div className="flex gap-2 flex-wrap">
                              {match.games.map((game, index) => {
                                const teamGameScore = isTeam1 ? game.team1Score : game.team2Score
                                const oppGameScore = isTeam1 ? game.team2Score : game.team1Score
                                const wonGame = teamGameScore > oppGameScore
                                
                                return (
                                  <div key={index} className={`rounded px-2 py-1 text-xs font-bold ${
                                    wonGame ? 'bg-pink-100 text-pink-700' : 'bg-rose-100 text-rose-700'
                                  }`}>
                                    Game {index + 1}: {teamGameScore}-{oppGameScore}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CategoryView
